/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import * as React from 'react';
import cx from 'classnames';

import {
    Icon,
    ModalActions,
    StyledModal,
    Switch,
    Button,
    DividerNew,
    Heading,
} from 'sarsaparilla';

import { useMap } from '../MapContext';

import { MapControlButton } from './MapControlButton';
import styles from './MapZoomControls.module.scss';

interface MapZoomControlsProps {
    className?: string;
}

const settingsLocalStorageKey = 'r1s:mapPanControls';

export function MapZoomControls({ className }: MapZoomControlsProps) {
    const [isMapSettingsModalOpen, setIsMapSettingsModalOpen] = React.useState(false);
    const [isMapPanControlsEnabled, setIsMapPanControlsEnabled] = React.useState(() => {
        try {
            return localStorage.getItem(settingsLocalStorageKey) === 'on';
        } catch {
            return false;
        }
    });

    const { mapInstance } = useMap();

    const handleZoomIn = () => {
        if (!mapInstance) return;
        mapInstance.zoomTo(mapInstance.getZoom() + 1);
    };

    const handleZoomOut = () => {
        if (!mapInstance) return;
        mapInstance.zoomTo(mapInstance.getZoom() - 1);
    };

    const handleToggleMapPanControlsChange = (isSelected: boolean) => {
        setIsMapPanControlsEnabled(isSelected);

        try {
            localStorage.setItem(settingsLocalStorageKey, isSelected ? 'on' : 'off');
        } catch {
            // Do nothing
        }
    };

    // Compute how far each arrow press should move the map
    const getPanDistance = React.useCallback(() => {
        if (!mapInstance) return 0;

        const panDistanceRatio = 0.2; // percentage used to compute pan distance
        const minPanDistancePx = 80; // minimum distance in pixels
        const { clientWidth, clientHeight } = mapInstance.getContainer();
        const shortestAxis = Math.min(clientWidth, clientHeight);

        return Math.max(minPanDistancePx, Math.round(shortestAxis * panDistanceRatio));
    }, [mapInstance]);

    // Translate a direction into x/y pixel offsets and pan smoothly.
    // Positive x moves right, positive y moves down.
    const panMap = React.useCallback(
        (direction: 'up' | 'right' | 'down' | 'left') => {
            if (!mapInstance) return;

            const panDistance = getPanDistance();
            if (!panDistance) return;

            const panByDirection: Record<
                'up' | 'right' | 'down' | 'left',
                [number, number]
            > = {
                up: [0, -panDistance],
                right: [panDistance, 0],
                down: [0, panDistance],
                left: [-panDistance, 0],
            };

            mapInstance.panBy(panByDirection[direction], { duration: 250 });
        },
        [getPanDistance, mapInstance]
    );

    if (!mapInstance) return null;

    return (
        <div className={cx(styles.zoomAndPanControls, className)}>
            <div className={styles.group} data-component="zoom-group">
                <MapControlButton
                    shape="square"
                    hasShadow={false}
                    hasInsetFocus
                    onClick={handleZoomIn}
                    aria-label="Zoom In"
                >
                    <Icon iconName="add" />
                </MapControlButton>
                <MapControlButton
                    shape="square"
                    hasShadow={false}
                    hasInsetFocus
                    onClick={handleZoomOut}
                    aria-label="Zoom Out"
                >
                    <Icon iconName="remove" />
                </MapControlButton>
            </div>

            <MapControlButton
                shape="circle"
                hasInsetFocus
                aria-label="Map Settings"
                aria-haspopup="dialog"
                aria-expanded={isMapSettingsModalOpen}
                onClick={() => {
                    setIsMapSettingsModalOpen((prev) => !prev);
                }}
                className={cx({
                    [styles.keyboardOnly]:
                        !isMapSettingsModalOpen && !isMapPanControlsEnabled,
                })}
            >
                <Icon iconName="cog" />
            </MapControlButton>

            {isMapPanControlsEnabled ? (
                <div className={styles.group} data-component="pan-group">
                    <MapControlButton
                        shape="square"
                        hasShadow={false}
                        hasInsetFocus
                        onClick={() => panMap('up')}
                        aria-label="Pan Up"
                    >
                        <Icon iconName="chevron-up" />
                    </MapControlButton>
                    <MapControlButton
                        shape="square"
                        hasShadow={false}
                        hasInsetFocus
                        onClick={() => panMap('down')}
                        aria-label="Pan Down"
                    >
                        <Icon iconName="chevron-down" />
                    </MapControlButton>
                    <MapControlButton
                        shape="square"
                        hasShadow={false}
                        hasInsetFocus
                        onClick={() => panMap('left')}
                        aria-label="Pan Left"
                    >
                        <Icon iconName="chevron-left" />
                    </MapControlButton>
                    <MapControlButton
                        shape="square"
                        hasShadow={false}
                        hasInsetFocus
                        onClick={() => panMap('right')}
                        aria-label="Pan Right"
                    >
                        <Icon iconName="chevron-right" />
                    </MapControlButton>
                </div>
            ) : null}

            <StyledModal
                isOpen={isMapSettingsModalOpen}
                onRequestClose={() => setIsMapSettingsModalOpen(false)}
                size="sm"
                shouldActionsStickInMobile={true}
            >
                <Heading
                    headingLevel={1}
                    appearance="h5"
                    className="pt-half pr-4"
                    id="modal-heading"
                >
                    Map Accessibility Settings
                </Heading>

                <DividerNew className="my-3" />

                <div className="pb-2">
                    <Switch
                        label="Map Pan Controls"
                        labelPosition="left"
                        description="Show directional buttons for panning the map"
                        isSelected={isMapPanControlsEnabled}
                        onChange={handleToggleMapPanControlsChange}
                    />
                </div>

                <ModalActions>
                    <Button
                        onClick={() => setIsMapSettingsModalOpen(false)}
                        shouldFitContainer
                    >
                        Done
                    </Button>
                </ModalActions>
            </StyledModal>
        </div>
    );
}
